<template>
  <div v-if="showThumbnail && (preparing || resolvedHostname)" class="site-qr" :class="{ 'site-qr--dark': dark, 'site-qr--compact': compact, 'site-qr--toolbar': toolbar }">
    <div v-if="preparing" class="site-qr-loading" role="status" aria-label="Preparing visitor QR code">
      <v-progress-circular indeterminate size="22" width="2" />
    </div>
    <button v-else-if="qrPreview" type="button" class="site-qr-button" title="Open and download visitor QR code" @click="open()">
      <img :src="qrPreview" :alt="`QR code for ${qrUrl}`" />
      <span>{{ label }}</span>
    </button>
  </div>

  <v-dialog v-model="dialogOpen" max-width="520">
    <v-card>
      <v-card-title>Visitor QR code</v-card-title>
      <v-card-text class="qr-dialog-content">
        <v-progress-circular v-if="preparing" indeterminate color="primary" aria-label="Generating QR code" />
        <template v-else-if="qrPreview">
          <img :src="qrPreview" :alt="`QR code for ${qrUrl}`" class="qr-preview" />
          <a :href="qrUrl" target="_blank" rel="noopener noreferrer" class="qr-url">{{ qrUrl }}</a>
          <p class="qr-print-note">Download the SVG for professional printing at any size.</p>
        </template>
        <v-alert v-if="qrError" type="error" variant="tonal">{{ qrError }}</v-alert>
      </v-card-text>
      <v-card-actions class="qr-actions">
        <v-btn variant="text" @click="dialogOpen = false">Close</v-btn>
        <v-spacer />
        <v-btn color="primary" prepend-icon="mdi-download" :loading="downloading === 'svg'" :disabled="preparing || Boolean(downloading) || !qrPreview" @click="download('svg')">Download SVG</v-btn>
        <v-btn variant="outlined" prepend-icon="mdi-download" :loading="downloading === 'png'" :disabled="preparing || Boolean(downloading) || !qrPreview" @click="download('png')">Download PNG</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import QRCode from 'qrcode';
import api from '@/api/client';

const props = defineProps({
  site: { type: Object, required: true },
  hostname: { type: String, default: '' },
  showThumbnail: { type: Boolean, default: true },
  compact: Boolean,
  dark: Boolean,
  toolbar: Boolean,
  label: { type: String, default: 'Visitor QR' },
});

const resolvedHostname = ref('');
const qrSvg = ref('');
const loading = ref(false);
const generating = ref(false);
const downloading = ref('');
const dialogOpen = ref(false);
const qrError = ref('');
let loadRequest = 0;

const qrUrl = computed(() => resolvedHostname.value ? `https://${resolvedHostname.value}` : '');
const qrPreview = computed(() => qrSvg.value ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrSvg.value)}` : '');
const preparing = computed(() => loading.value || generating.value);
const qrOptions = { errorCorrectionLevel: 'H', margin: 4, color: { dark: '#000000', light: '#ffffff' } };

watch([() => props.hostname, () => props.site.id], async ([hostname]) => {
  const request = ++loadRequest;
  qrError.value = '';
  qrSvg.value = '';
  resolvedHostname.value = hostname;
  if (hostname) {
    await generate(hostname, request);
    return;
  }
  if (!props.showThumbnail) return;
  loading.value = true;
  try {
    const { data } = await api.get(`/sites/${props.site.id}/hostnames`);
    if (request !== loadRequest) return;
    resolvedHostname.value = data.find(value => typeof value === 'string') || '';
    if (resolvedHostname.value) await generate(resolvedHostname.value, request);
  } catch {
    if (request === loadRequest) qrError.value = 'Could not load the visitor QR code.';
  } finally {
    if (request === loadRequest) loading.value = false;
  }
}, { immediate: true });

async function generate(hostname, request = loadRequest) {
  generating.value = true;
  try {
    const svg = await QRCode.toString(`https://${hostname}`, { ...qrOptions, type: 'svg', width: 1200 });
    if (request === loadRequest) qrSvg.value = svg;
  } catch {
    if (request === loadRequest) qrError.value = 'Could not generate the QR code. Please try again.';
  } finally {
    if (request === loadRequest) generating.value = false;
  }
}

async function open(hostname = resolvedHostname.value) {
  if (hostname && hostname !== resolvedHostname.value) {
    const request = ++loadRequest;
    resolvedHostname.value = hostname;
    qrSvg.value = '';
    qrError.value = '';
    dialogOpen.value = true;
    await generate(hostname, request);
    return;
  }
  dialogOpen.value = true;
}

function triggerDownload(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function download(format) {
  if (!qrUrl.value || downloading.value) return;
  downloading.value = format;
  qrError.value = '';
  const filename = `visitor-qr-${resolvedHostname.value.replace(/[^a-z0-9.-]+/gi, '-')}`;
  try {
    if (format === 'svg') {
      const blobUrl = URL.createObjectURL(new Blob([qrSvg.value], { type: 'image/svg+xml;charset=utf-8' }));
      triggerDownload(blobUrl, `${filename}.svg`);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
    } else {
      const pngUrl = await QRCode.toDataURL(qrUrl.value, { ...qrOptions, type: 'image/png', width: 2400 });
      triggerDownload(pngUrl, `${filename}.png`);
    }
  } catch {
    qrError.value = 'Could not download the QR code. Please try again.';
  } finally {
    downloading.value = '';
  }
}

defineExpose({ open });
</script>

<style scoped>
.site-qr { flex: none; }
.site-qr-button { display: grid; justify-items: center; gap: 3px; padding: 3px; border: 0; border-radius: 5px; background: transparent; color: var(--ww-teal); cursor: pointer; }
.site-qr-button img, .site-qr-loading { width: 66px; height: 66px; border: 3px solid #fff; border-radius: 4px; background: #fff; }
.site-qr-button img { display: block; }
.site-qr-button span { font-size: 11px; font-weight: 700; line-height: 1.2; }
.site-qr-button:hover, .site-qr-button:focus-visible { background: #e9f2f0; }
.site-qr-button:focus-visible { outline: 2px solid var(--ww-teal); outline-offset: 2px; }
.site-qr-loading { display: grid; place-items: center; color: var(--ww-teal); }
.site-qr--compact .site-qr-button img, .site-qr--compact .site-qr-loading { width: 58px; height: 58px; }
.site-qr--toolbar .site-qr-button { display: flex; align-items: center; justify-content: flex-start; gap: 9px; width: 148px; height: 46px; padding: 3px 10px 3px 3px; border: 1px solid #81aaa8; background: #ffffff12; }
.site-qr--toolbar .site-qr-button img, .site-qr--toolbar .site-qr-loading { width: 38px; height: 38px; border-width: 2px; border-radius: 3px; }
.site-qr--toolbar .site-qr-button span { font-size: 11px; white-space: nowrap; }
.site-qr--toolbar .site-qr-loading { width: 148px; height: 46px; border: 1px solid #81aaa8; background: #ffffff12; }
.site-qr--dark .site-qr-button { color: #fff; }
.site-qr--dark .site-qr-button:hover, .site-qr--dark .site-qr-button:focus-visible { background: #0f3e42; }
.site-qr--dark .site-qr-button:focus-visible { outline-color: #fff; }
.qr-dialog-content { min-height: 360px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; text-align: center; }
.qr-preview { display: block; width: min(280px, 100%); aspect-ratio: 1; }
.qr-url { color: #1e6d6f; font-weight: 600; overflow-wrap: anywhere; }
.qr-print-note { margin: 0; color: #596575; font-size: 14px; }
.qr-actions { flex-wrap: wrap; gap: 8px; }
</style>
